import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import MultiCheckbox from "@/components/MultiCheckbox";
import { SUBJECTS, CLASS_LEVELS } from "@/constants/options";
import { Plus, Trash2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = () => ({ name: "", class_level: "", subjects: [], address: "" });

export default function FamilyProfileSetup() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [children, setChildren] = useState([empty()]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/family/profile");
        if (data.children?.length) setChildren(data.children.map((c) => ({
          name: c.name || "",
          class_level: c.class_level || "",
          subjects: c.subjects || [],
          address: c.address || "",
        })));
      } catch {}
    })();
  }, []);

  const update = (i, key, val) => {
    setChildren((cs) => cs.map((c, idx) => (idx === i ? { ...c, [key]: val } : c)));
  };
  const add = () => setChildren((cs) => [...cs, empty()]);
  const remove = (i) => setChildren((cs) => cs.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    for (const c of children) {
      if (!c.name || !c.class_level || !c.address) return toast.error("Tüm alanları doldur");
      if (!c.subjects.length) return toast.error("En az bir ders seç");
    }
    setLoading(true);
    try {
      await api.post("/family/profile", { children });
      await refresh();
      toast.success("Profilin kaydedildi");
      nav("/ara");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="reveal">
          <span className="text-xs uppercase tracking-widest text-neutral-500 font-medium">Aile kurulumu</span>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="family-setup-title">
            Çocuklarının bilgilerini ekle
          </h1>
          <p className="mt-3 text-neutral-500">Doğru eşleşme için gerekli bilgileri topluyoruz.</p>
        </div>

        <form onSubmit={submit} className="mt-10 space-y-8">
          {children.map((c, i) => (
            <div key={i} className="border border-neutral-200 rounded-2xl p-6 sm:p-8 bg-white reveal" data-testid={`child-block-${i}`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-xl font-semibold">Çocuk {i + 1}</h2>
                {children.length > 1 && (
                  <button type="button" onClick={() => remove(i)} className="text-sm text-neutral-500 hover:text-red-600 inline-flex items-center gap-1" data-testid={`remove-child-${i}`}>
                    <Trash2 size={14} /> Kaldır
                  </button>
                )}
              </div>

              <div className="space-y-5">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-500 font-medium">Ad Soyad</Label>
                  <Input required data-testid={`child-name-${i}`} value={c.name} onChange={(e) => update(i, "name", e.target.value)} className="mt-2 h-12" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-500 font-medium">Sınıf</Label>
                  <Select value={c.class_level} onValueChange={(v) => update(i, "class_level", v)}>
                    <SelectTrigger className="mt-2 h-12" data-testid={`child-class-${i}`}>
                      <SelectValue placeholder="Sınıf seç" />
                    </SelectTrigger>
                    <SelectContent>
                      {CLASS_LEVELS.map((cl) => (
                        <SelectItem key={cl} value={cl}>{cl}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-500 font-medium mb-3 block">Almak istediği dersler</Label>
                  <MultiCheckbox
                    options={SUBJECTS}
                    values={c.subjects}
                    onChange={(v) => update(i, "subjects", v)}
                    testidPrefix={`child-subject-${i}`}
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-neutral-500 font-medium">Dersin yapılacağı adres</Label>
                  <Input required data-testid={`child-address-${i}`} value={c.address} onChange={(e) => update(i, "address", e.target.value)} placeholder="Mahalle, ilçe, şehir" className="mt-2 h-12" />
                </div>
              </div>
            </div>
          ))}

          <button type="button" onClick={add} data-testid="add-child-btn"
            className="w-full border border-dashed border-neutral-300 rounded-2xl py-5 text-sm font-medium text-neutral-700 hover:border-neutral-900 hover:bg-neutral-50 transition-colors inline-flex items-center justify-center gap-2">
            <Plus size={16} /> Çocuk Ekle
          </button>

          <Button type="submit" disabled={loading} data-testid="family-setup-submit"
            className="w-full h-12 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white text-base shadow-md shadow-blue-500/20">
            {loading ? "Kaydediliyor..." : "Kaydet ve Öğretmen Ara"}
          </Button>
        </form>
      </div>
    </Layout>
  );
}
